import './styles.css';

import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { EmptyGraph } from 'components/Graph/EmptyGraph/EmptyGraph';
import {
  GraphContext,
  useGraphContextSelectors,
} from 'components/Graph/GraphContext';
import { selectComparingSameSnapshots } from 'components/Graph/model/selectors/overlay/selectComparingSameSnapshots';
import { selectGraphHasContent } from 'components/Graph/model/selectors/selectGraphHasContent';
import { selectGraphLoading } from 'components/Graph/model/selectors/selectGraphLoading';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { memo } from 'react';
import type { ReactElement } from 'react';
import { usePrevious } from 'utils/hooks/usePrevious';
import { simpleTypeMemo } from 'utils/react/simpleTypeMemo';
import { withModelProvider } from 'utils/react/withModelProvider';
import { GraphSameSnapshotsOverlay } from './GraphOverlay/GraphSameSnapshotsOverlay';
import { GraphSidePanel } from './GraphSidePanel/GraphSidePanel';
import { GraphSidePanelContent } from './GraphSidePanel/GraphSidePanelContent';
import { GraphToolbar } from './GraphToolbar/GraphToolbar';
import { LoadedGraph } from './LoadedGraph';

export const Graph = withModelProvider(GraphContext)(
  simpleTypeMemo((): ReactElement => {
    const msg = useLocale();
    const ctxProps = useGraphContextSelectors({
      graphHasContent: selectGraphHasContent,
      graphIsLoading: selectGraphLoading,
      graphLoadingState: prop('loading'),
      longRendering: prop('longRendering'),
      comparingSameSnapshots: selectComparingSameSnapshots,
    });
    const previousLoadingState = usePrevious<LoadingState>(
      ctxProps.graphLoadingState,
    );

    const showGraphError =
      ctxProps.graphLoadingState === 'ERROR' ||
      (previousLoadingState === 'ERROR' &&
        ctxProps.graphLoadingState === 'LOADING');

    const loadingMessage =
      msg.canvasGraph.loadingStates[ctxProps.graphLoadingState] ??
      (ctxProps.longRendering ? msg.canvasGraph.loadingStates.RENDERING : '');
    return (
      <div className='relative mb-0 flex flex-1 overflow-hidden'>
        <GraphSidePanel />
        <div className='flex flex-1 flex-col overflow-hidden'>
          <div className='flex flex-1 flex-row overflow-hidden'>
            <GraphSidePanelContent />
            {/* Extra container needed for proper alignment of load view preview */}
            <div className='relative flex flex-1 overflow-hidden'>
              {ctxProps.comparingSameSnapshots && <GraphSameSnapshotsOverlay />}
              {!showGraphError &&
                (ctxProps.graphHasContent ? <LoadedGraph /> : <EmptyGraph />)}
              {showGraphError && <GraphError />}
              <GraphToolbar />
            </div>
            <GraphLoader
              visible={ctxProps.graphIsLoading}
              message={loadingMessage}
            />
          </div>
        </div>
      </div>
    );
  }),
);

const BareGraphError = () => {
  const msg = useLocale();
  return (
    <div className='flex flex-1 flex-col justify-center'>
      <p className='text-danger mt-lgu-10 text-center'>
        {msg.canvasGraph.loadingStates.ERROR}
      </p>
    </div>
  );
};
const GraphError = memo(BareGraphError);

const BareGraphLoader = (props: { message: string; visible: boolean }) => (
  <div
    className={classNames(
      'graph__overlay-wrapper',
      !props.visible && 'graph__overlay-wrapper--hidden',
    )}
  >
    <div className='graph__overlay flex flex-col justify-center'>
      <LoaderAndText bottomText={props.message ?? ''} />
    </div>
  </div>
);

export const GraphLoader = memo(BareGraphLoader);
